"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Menu, X, ChevronDown, ArrowRight, MapPin, Briefcase, Sparkles, Download } from "lucide-react"
import Link from "next/link"
import Image from "next/image"

type SimpleLink = { label: string; href: string; description?: string }
type MegaGroup = { title: string; href?: string; items: SimpleLink[] }
type MegaMenu = {
  groups: MegaGroup[]
  featured?: {
    eyebrow: string
    title: string
    description: string
    href: string
    cta: string
    icon?: "sparkles" | "map" | "briefcase"
  }
}
type NavLink = {
  label: string
  href: string
  highlight?: boolean
  children?: SimpleLink[]
  mega?: MegaMenu
}

const navLinks: NavLink[] = [
  {
    label: "Accounting & Finance",
    href: "/industries/accounting-finance",
    highlight: true,
    mega: {
      groups: [
        {
          title: "Leadership Roles",
          href: "/industries/accounting-finance",
          items: [
            { label: "Accounting & Finance", href: "/industries/accounting-finance" },
            { label: "CFO & VP Finance Search", href: "/industries/accounting-finance/cfo-vp-finance-search" },
            { label: "Controller Recruiting", href: "/industries/accounting-finance/controller-recruiting" },
            { label: "Finance Director Search", href: "/industries/accounting-finance/finance-director-search" },
            { label: "FP&A Recruiting", href: "/industries/accounting-finance/fpa-recruiting" },
            { label: "Interim & Contract Finance", href: "/industries/accounting-finance/interim-contract-finance" },
          ],
        },
        {
          title: "Specialty Roles",
          items: [
            { label: "Tax & Audit Staffing", href: "/industries/accounting-finance/tax-audit-staffing" },
            { label: "Internal Audit Recruiting", href: "/industries/accounting-finance/internal-audit-recruiting" },
            { label: "Treasury & Cash Management", href: "/industries/accounting-finance/treasury-cash-management" },
            { label: "Cost Accountant Recruiting", href: "/industries/accounting-finance/cost-accountant-recruiting" },
            { label: "Staff Accountant Staffing", href: "/industries/accounting-finance/staff-accountant-staffing" },
            { label: "AP / AR Recruiting", href: "/industries/accounting-finance/ap-ar-recruiting" },
            { label: "Payroll Specialist", href: "/industries/accounting-finance/payroll-specialist-recruiting" },
            { label: "Bookkeeper & Clerk Staffing", href: "/industries/accounting-finance/bookkeeper-clerk-staffing" },
            { label: "ERP Implementation (SAP/NetSuite/Oracle)", href: "/industries/accounting-finance/erp-implementation-staffing" },
          ],
        },
        {
          title: "By Market",
          href: "/markets",
          items: [
            { label: "Los Angeles", href: "/markets/los-angeles/accounting-finance-recruiter" },
            { label: "— El Segundo / South Bay", href: "/markets/los-angeles/el-segundo-south-bay" },
            { label: "— Pasadena", href: "/markets/los-angeles/pasadena" },
            { label: "— Long Beach", href: "/markets/los-angeles/long-beach" },
            { label: "— Woodland Hills / SFV", href: "/markets/los-angeles/woodland-hills-sfv" },
            { label: "Orange County", href: "/markets/orange-county/accounting-finance-recruiter" },
            { label: "— Irvine", href: "/markets/orange-county/irvine" },
            { label: "— Costa Mesa", href: "/markets/orange-county/costa-mesa" },
            { label: "— Anaheim", href: "/markets/orange-county/anaheim" },
            { label: "San Diego", href: "/markets/san-diego/accounting-finance-recruiter" },
            { label: "— Downtown San Diego", href: "/markets/san-diego/downtown" },
            { label: "— Carlsbad / North County", href: "/markets/san-diego/carlsbad-north-county" },
          ],
        },
      ],
      featured: {
        eyebrow: "2026 Salary Guide",
        title: "2026 Accounting & Finance Compensation Report",
        description:
          "Benchmark salaries across 40+ roles in Southern California. Data-driven hiring starts with current market intel.",
        href: "/resources/2026-salary-guide",
        cta: "Download Free Guide",
        icon: "briefcase",
      },
    },
  },
  {
    label: "Other Industries",
    href: "/industries",
    children: [
      { label: "Engineering & Technical", href: "/industries/engineering-technical" },
      { label: "Engineering Recruiting", href: "/industries/engineering-technical/engineering-recruiting" },
      { label: "Technical & IT Staffing", href: "/industries/engineering-technical/technical-it-staffing" },
      { label: "Human Resources", href: "/industries/human-resources" },
      { label: "HR Generalist Recruiting", href: "/industries/human-resources/hr-generalist-recruiting" },
      { label: "Talent Acquisition Roles", href: "/industries/human-resources/talent-acquisition-recruiting" },
    ],
  },
  {
    label: "Employers",
    href: "/employers",
    children: [
      { label: "Why Hire TSG?", href: "/employers/why-tsg" },
      { label: "Our Process", href: "/employers/our-process" },
      { label: "Submit a Job", href: "/employers/submit-a-job" },
      { label: "Reviews & Case Studies", href: "/employers/reviews-case-studies" },
      { label: "Vytal On Demand", href: "/employers/vytal-on-demand" },
      { label: "Salary Guide", href: "/employers/salary-guide" },
    ],
  },
  {
    label: "Candidates",
    href: "/candidates",
    children: [
      { label: "Current Openings", href: "/candidates/current-openings" },
      { label: "Submit Your Resume", href: "/candidates/submit-resume" },
      { label: "Candidate Services", href: "/candidates/services" },
      { label: "Current Consultants", href: "/candidates/current-consultants" },
      { label: "Salary Guide", href: "/candidates/salary-guide" },
      { label: "Career Resources", href: "/candidates/resources" },
    ],
  },
  {
    label: "About",
    href: "/about",
    children: [
      { label: "Why TSG?", href: "/about/why-tsg" },
      { label: "Leadership", href: "/about/leadership" },
      { label: "Awards & Affiliations", href: "/about/awards" },
      { label: "TSG Impact", href: "/about/impact" },
      { label: "Careers at TSG", href: "/about/careers" },
      { label: "Contact", href: "/contact" },
    ],
  },
  { label: "Insights", href: "/insights" },
]

const featuredIcon = {
  sparkles: Sparkles,
  map: MapPin,
  briefcase: Download,
} as const

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const scheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 120)
  }, [])

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const openDropdown = useCallback((label: string) => {
    cancelClose()
    setActiveDropdown(label)
  }, [cancelClose])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", handleScroll)
      if (closeTimer.current) clearTimeout(closeTimer.current)
    }
  }, [])

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.08)] py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2 shrink-0">
          <Image
            src="/images/tsg-logo.png"
            alt="Transition Staffing Group — We Build Winning Teams"
            width={180}
            height={36}
            className={`h-8 w-auto transition-all duration-300 ${
              scrolled ? "brightness-100" : "brightness-0 invert"
            }`}
            priority
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-0.5">
          {navLinks.map((link) => {
            const hasMega = !!link.mega
            const hasChildren = !!link.children && link.children.length > 0
            const isOpen = activeDropdown === link.label

            const baseTextClass = scrolled
              ? "text-foreground/80 hover:text-foreground hover:bg-muted"
              : "text-white/90 hover:text-white hover:bg-white/10"

            const highlightClass = link.highlight
              ? scrolled
                ? "text-primary hover:text-primary hover:bg-primary/5"
                : "text-white hover:text-white hover:bg-white/10"
              : baseTextClass

            if (!hasMega && !hasChildren) {
              return (
                <Link
                  key={link.label}
                  href={link.href}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${highlightClass}`}
                >
                  {link.label}
                </Link>
              )
            }

            return (
              <div
                key={link.label}
                className={hasMega ? "" : "relative"}
                onMouseEnter={() => openDropdown(link.label)}
                onMouseLeave={scheduleClose}
              >
                <button
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${highlightClass} ${link.highlight ? "font-semibold" : ""}`}
                  aria-expanded={isOpen}
                >
                  {link.highlight && (
                    <span className={`mr-0.5 inline-block w-1.5 h-1.5 rounded-full ${scrolled ? "bg-amber-500" : "bg-amber-400"}`} />
                  )}
                  {link.label}
                  <ChevronDown className={`h-3.5 w-3.5 opacity-60 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>

                {/* Simple dropdown */}
                {isOpen && hasChildren && !hasMega && (
                  <div
                    className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl ring-1 ring-black/5 py-2 animate-in fade-in zoom-in-95 duration-150"
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                  >
                    {link.children!.map((child) => (
                      <Link
                        key={child.label}
                        href={child.href}
                        className="block px-4 py-2.5 text-sm text-foreground/80 hover:text-foreground hover:bg-muted transition-colors"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}

                {/* Mega menu */}
                {isOpen && hasMega && (
                  <div
                    className="fixed left-0 right-0 top-full mt-1 px-4 sm:px-6 lg:px-8 animate-in fade-in slide-in-from-top-2 duration-150"
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                  >
                    <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-2xl ring-1 ring-black/5 overflow-hidden">
                      <div className="grid grid-cols-12 gap-0">
                        {/* Groups */}
                        <div className="col-span-12 lg:col-span-9 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-2 p-6 lg:p-8">
                          {link.mega!.groups.map((group) => (
                            <div key={group.title}>
                              <div className="mb-3">
                                {group.href ? (
                                  <Link
                                    href={group.href}
                                    className="group inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.08em] text-primary hover:text-primary/80"
                                  >
                                    {group.title}
                                    <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                                  </Link>
                                ) : (
                                  <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                                    {group.title}
                                  </span>
                                )}
                              </div>
                              <ul className="space-y-1">
                                {group.items.map((item) => (
                                  <li key={item.label}>
                                    <Link
                                      href={item.href}
                                      className={`block px-2 py-1.5 -mx-2 rounded-md text-sm transition-colors ${
                                        item.label.startsWith("—")
                                          ? "text-foreground/55 hover:text-foreground hover:bg-muted pl-4"
                                          : "text-foreground/75 hover:text-foreground hover:bg-muted"
                                      }`}
                                    >
                                      {item.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>

                        {/* Featured card */}
                        {link.mega!.featured && (
                          <div className="col-span-12 lg:col-span-3 bg-gradient-to-br from-[hsl(85,54%,51%)] to-[hsl(85,45%,40%)] text-white p-6 lg:p-8 flex flex-col justify-between rounded-r-xl relative overflow-hidden">
                            {/* Decorative background circles */}
                            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 pointer-events-none" />
                            <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-white/8 pointer-events-none" />
                            <div>
                              {(() => {
                                const Icon = featuredIcon[link.mega!.featured!.icon ?? "sparkles"]
                                return (
                                  <div className="inline-flex items-center gap-1.5 rounded-full bg-white/20 ring-1 ring-white/30 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white mb-4">
                                    <Icon className="h-3 w-3" />
                                    {link.mega!.featured!.eyebrow}
                                  </div>
                                )
                              })()}
                              <h3 className="text-base font-bold leading-tight mb-2 text-white">
                                {link.mega!.featured!.title}
                              </h3>
                              <p className="text-sm text-white/80 leading-relaxed">
                                {link.mega!.featured!.description}
                              </p>
                            </div>
                            <Link
                              href={link.mega!.featured!.href}
                              className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-white hover:text-white/90 group"
                            >
                              {link.mega!.featured!.cta}
                              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-3">
          <Button
            variant={scrolled ? "outline" : "hero-outline"}
            size="sm"
            className={scrolled ? "" : "border-white/30 text-white hover:bg-white/10 hover:text-white hover:border-white/60"}
            asChild
          >
            <Link href="/candidates/current-openings">Find Jobs</Link>
          </Button>
          <Button
            size="sm"
            className={`font-semibold ${
              scrolled ? "" : "bg-amber-500 hover:bg-amber-600 text-white shadow-[0_4px_20px_-4px_rgba(245,158,11,0.4)]"
            }`}
            asChild
          >
            <Link href="/employers/submit-a-job">Hire Talent</Link>
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          className={`lg:hidden p-2 rounded-md ${
            scrolled ? "text-foreground" : "text-white"
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-border animate-in slide-in-from-top-2 duration-200">
          <nav className="max-h-[calc(100vh-80px)] overflow-y-auto px-4 py-4 space-y-1">
            {navLinks.map((link) => {
              const hasMega = !!link.mega
              const hasChildren = !!link.children && link.children.length > 0

              if (!hasMega && !hasChildren) {
                return (
                  <Link
                    key={link.label}
                    href={link.href}
                    className="block px-3 py-2.5 rounded-md text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-muted"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                )
              }

              return (
                <details key={link.label} className="group">
                  <summary className={`flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-medium hover:bg-muted cursor-pointer list-none ${
                    link.highlight ? "text-primary font-semibold" : "text-foreground/80 hover:text-foreground"
                  }`}>
                    <span className="flex items-center gap-2">
                      {link.highlight && <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />}
                      {link.label}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-60 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="ml-3 mt-1 mb-2 border-l border-border pl-3 space-y-3">
                    {hasMega
                      ? link.mega!.groups.map((group) => (
                          <div key={group.title}>
                            <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                              {group.title}
                            </div>
                            <div className="space-y-0.5">
                              {group.items.map((item) => (
                                <Link
                                  key={item.label}
                                  href={item.href}
                                  className={`block px-2 py-1.5 rounded-md text-sm hover:bg-muted ${
                                    item.label.startsWith("—")
                                      ? "text-foreground/55 hover:text-foreground pl-4"
                                      : "text-foreground/70 hover:text-foreground"
                                  }`}
                                  onClick={() => setMobileOpen(false)}
                                >
                                  {item.label}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))
                      : link.children!.map((child) => (
                          <Link
                            key={child.label}
                            href={child.href}
                            className="block px-2 py-2 rounded-md text-sm text-foreground/70 hover:text-foreground hover:bg-muted"
                            onClick={() => setMobileOpen(false)}
                          >
                            {child.label}
                          </Link>
                        ))}
                  </div>
                </details>
              )
            })}
            <div className="pt-4 space-y-2 border-t border-border mt-4">
              <Button className="w-full" variant="outline" asChild>
                <Link href="/candidates/current-openings">Find Jobs</Link>
              </Button>
              <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold" asChild>
                <Link href="/employers/submit-a-job">Hire Talent</Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
